import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

interface IUseIframeMessagingOptions {
  /** Origin to accept messages from */
  origin: string;
  /** Handler for incoming messages */
  onMessage: (event: MessageEvent) => void;
  /** Function to send initial config when iframe is ready */
  sendConfig: () => void;
  /** Optional external ref to use instead of creating one internally */
  iframeRef?: RefObject<HTMLIFrameElement>;
}

interface IUseIframeMessagingReturn {
  /** Ref to attach to the iframe element */
  iframeRef: RefObject<HTMLIFrameElement>;
}

/**
 * Hook to manage iframe postMessage communication.
 * Handles message listener setup/cleanup and iframe load events.
 */
export function useIframeMessaging({
  origin,
  onMessage,
  sendConfig,
  iframeRef: externalRef,
}: IUseIframeMessagingOptions): IUseIframeMessagingReturn {
  const internalRef = useRef<HTMLIFrameElement>(null);
  const iframeRef = externalRef ?? internalRef;

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== origin) {
        return;
      }
      onMessage(event);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [origin, onMessage]);

  // Send config when iframe loads
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const handleLoad = (): void => {
      sendConfig();
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [sendConfig]);

  return { iframeRef };
}
